import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import { environment } from '@env/environment';
import { AlertService } from '@services/alert.service';
import { TokenService } from '@services/token.service';
import { WidgetsService } from '@services/widgets.service';
import { Alert } from '../../models';
import { enumAlertType } from '../../enums';
import { CompaniesService } from '@services/companies.service';
import { ApplicationModeService } from '@services/application-mode.service';
import { enumApplicationMode } from '@enum/enumApplicationMode';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface IToastTimer {
  handle: ReturnType<typeof setTimeout>;
  remaining: number;
  start: number;
}

const TOAST_LEAVE_ANIMATION_MS: number = 320;

@Component({
  // moduleId: module.id,
  // tslint:disable-next-line:component-selector
  selector: 'alert',
  templateUrl: 'alert.component.html',
  styleUrls: ['./alert.component.scss'],
})
export class AlertComponent implements OnInit, OnDestroy {
  @Input() public id: string;

  public alerts: Alert[] = [];
  public modalAlerts: Alert[] = [];
  public enumAlertType: typeof enumAlertType = enumAlertType;
  public alertLifeMs: number = environment.alertTime;
  public leavingAlerts: Set<Alert> = new Set<Alert>();

  public modalErrorShown: boolean;
  public modalSuccessShown: boolean;
  public modalInfoShown: boolean;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();
  private timers: Map<Alert, IToastTimer> = new Map<Alert, IToastTimer>();
  private leaveTimers: Set<ReturnType<typeof setTimeout>> = new Set<ReturnType<typeof setTimeout>>();

  constructor(
    private alertService: AlertService,
    private tokenService: TokenService,
    private widgetsService: WidgetsService,
    private companiesService: CompaniesService,
    private applicationModeService: ApplicationModeService,
  ) {}

  public ngOnInit(): void {
    this.alertService
      .getAlert(this.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((alert: Alert) => {
        if (!alert.title && alert.type !== enumAlertType.ModalInfo) {
          this.clearAllTimers();
          this.alerts = [];
          this.modalAlerts = [];
          this.leavingAlerts.clear();
          return;
        }

        if (
          alert.type === enumAlertType.ModalError ||
          alert.type === enumAlertType.ModalSuccess ||
          alert.type === enumAlertType.ModalInfo
        ) {
          this.modalAlerts.push(alert);

          switch (alert.type) {
            case enumAlertType.ModalError:
              this.modalErrorShown = true;
              break;
            case enumAlertType.ModalSuccess:
              this.modalSuccessShown = true;
              break;
            case enumAlertType.ModalInfo:
              this.modalInfoShown = true;
              break;
            default:
              break;
          }
        } else {
          this.alerts.push(alert);
        }

        if (this.tokenService.isAuthenticated && alert?.isCloseAfterTimeout) {
          this.armTimer(alert, this.alertLifeMs);
        }
      });
  }

  public ngOnDestroy(): void {
    this.clearAllTimers();
    this.leaveTimers.forEach((handle: ReturnType<typeof setTimeout>) => clearTimeout(handle));
    this.leaveTimers.clear();
    this.destroy$.next();
  }

  public removeAlert(alert: Alert): void {
    this.clearTimer(alert);
    this.leavingAlerts.delete(alert);
    this.alerts = this.alerts.filter((x) => x !== alert);
    this.modalAlerts = this.modalAlerts.filter((x) => x !== alert);
    if (!this.modalAlerts.length) {
      this.modalErrorShown = false;
      this.modalSuccessShown = false;
      this.modalInfoShown = false;
    }
  }

  public dismiss(alert: Alert): void {
    if (this.leavingAlerts.has(alert)) {
      return;
    }
    this.clearTimer(alert);
    this.leavingAlerts.add(alert);
    const handle: ReturnType<typeof setTimeout> = setTimeout(() => {
      this.leaveTimers.delete(handle);
      this.removeAlert(alert);
    }, TOAST_LEAVE_ANIMATION_MS);
    this.leaveTimers.add(handle);
  }

  public onHover(alert: Alert): void {
    const timer: IToastTimer | undefined = this.timers.get(alert);
    if (!timer) {
      return;
    }
    clearTimeout(timer.handle);
    timer.remaining = Math.max(0, timer.remaining - (Date.now() - timer.start));
  }

  public onLeave(alert: Alert): void {
    const timer: IToastTimer | undefined = this.timers.get(alert);
    if (!timer) {
      return;
    }
    this.armTimer(alert, timer.remaining);
  }

  public contactToSupport(): void {
    this.widgetsService.trackData('Contact team:', { error: 'Server Error' });
    this.widgetsService.openWidget();
  }

  public typeClass(alert: Alert): string {
    if (!alert) {
      return '';
    }
    switch (alert.type) {
      case enumAlertType.Success:
        return 'success';
      case enumAlertType.Error:
        return 'error';
      case enumAlertType.Warning:
        return 'warning';
      case enumAlertType.Info:
        return 'info';
      default:
        return '';
    }
  }

  // Legacy alert markup classes — used by the old design branch (regular users) until the new toast design is approved.
  public cssClass(alert: Alert): string {
    if (!alert) {
      return '';
    }
    switch (alert.type) {
      case enumAlertType.Success:
        return 'alert alert-success';
      case enumAlertType.Error:
        return 'alert alert-danger';
      case enumAlertType.Info:
        return 'alert alert-info';
      case enumAlertType.Warning:
        return 'alert alert-warning';
      default:
        return '';
    }
  }

  public get isAdminOrSupport(): boolean {
    return this.companiesService.isSupportOrAdmin();
  }

  // New toast design is shown only on the management side (SUPPORT application mode, which covers
  // support/admin/super + customs broker); the client side (USER mode) keeps the legacy alert design
  // until the new design is approved. Keyed off application mode (not roles) so a multi-role account
  // viewing the client part still sees the legacy design. Guarded by auth so login/confirm-email,
  // where localStorage['application_mode'] may be stale, always show the legacy design.
  public get useNewToastDesign(): boolean {
    return this.tokenService.isAuthenticated && this.applicationModeService.activeApplicationMode === enumApplicationMode.SUPPORT;
  }

  private armTimer(alert: Alert, duration: number): void {
    const handle: ReturnType<typeof setTimeout> = setTimeout(() => this.dismiss(alert), duration);
    this.timers.set(alert, { handle, remaining: duration, start: Date.now() });
  }

  private clearTimer(alert: Alert): void {
    const timer: IToastTimer | undefined = this.timers.get(alert);
    if (!timer) {
      return;
    }
    clearTimeout(timer.handle);
    this.timers.delete(alert);
  }

  private clearAllTimers(): void {
    this.timers.forEach((timer: IToastTimer) => clearTimeout(timer.handle));
    this.timers.clear();
  }
}
